import React, { useEffect, useState } from 'react';
import { useKeycloak } from '@react-keycloak/web';
import { X, FileText, Loader2, AlertCircle } from 'lucide-react';
import { FEATURED_DOC } from './featuredDoc';
import { fetchDocumentObjectUrl } from './api';

interface DocViewerModalProps {
  open: boolean;
  onClose: () => void;
  docId?: string;
  title?: string;
}

/**
 * Full-screen in-app viewer for a Keycloak-gated document.
 *
 * The PDF is streamed from a PRIVATE blob through the backend
 * (GET /documents/{id}) with the Keycloak bearer token in the request
 * header. We turn the response into a local object URL and render it in the
 * browser's native PDF viewer. No storage/SAS/blob URL is ever exposed —
 * the token stays in the header, and the object URL is revoked on close.
 */
const DocViewerModal: React.FC<DocViewerModalProps> = ({
  open,
  onClose,
  docId = FEATURED_DOC.id,
  title = FEATURED_DOC.title,
}) => {
  const { keycloak } = useKeycloak();
  const [objectUrl, setObjectUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Esc to close + lock background scroll while open
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose]);

  // Fetch the document (authenticated) when opened; revoke the URL on close.
  useEffect(() => {
    if (!open) return;
    let revoked = false;
    let createdUrl: string | null = null;

    const load = async () => {
      setError(null);
      setLoading(true);
      try {
        const token = keycloak?.token;
        if (!token) throw new Error('Not authenticated');
        const url = await fetchDocumentObjectUrl(docId, token);
        if (revoked) {
          URL.revokeObjectURL(url);
          return;
        }
        createdUrl = url;
        setObjectUrl(url);
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Unable to load document');
      } finally {
        if (!revoked) setLoading(false);
      }
    };

    void load();

    return () => {
      revoked = true;
      if (createdUrl) URL.revokeObjectURL(createdUrl);
      setObjectUrl(null);
    };
  }, [open, docId, keycloak]);

  if (!open) return null;

  return (
    <div
      className="doc-modal-overlay"
      role="dialog"
      aria-modal="true"
      aria-label={title}
      onClick={onClose}
    >
      <div className="doc-modal" onClick={(e) => e.stopPropagation()}>
        <div className="doc-modal-head">
          <div className="doc-modal-title">
            <FileText size={18} />
            <span>{title}</span>
          </div>
          <div className="doc-modal-actions">
            <button className="doc-modal-close" onClick={onClose} aria-label="Close">
              <X size={20} />
            </button>
          </div>
        </div>
        <div className="doc-modal-body">
          {loading && (
            <div className="doc-modal-state">
              <Loader2 className="doc-modal-spin" size={30} />
              <p>Loading document…</p>
            </div>
          )}
          {error && !loading && (
            <div className="doc-modal-state doc-modal-error">
              <AlertCircle size={28} />
              <p>{error}</p>
            </div>
          )}
          {objectUrl && !loading && !error && (
            <iframe
              className="doc-modal-frame"
              src={objectUrl}
              title={title}
              frameBorder={0}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default DocViewerModal;
